/**
 * FELVORA — Vertical Intermediación de Inmuebles.
 * Widget de PERFIL TRANSACCIONAL del cliente (feb 2026 · iter 07-31).
 *
 * Muestra el acumulado 6 meses por fracción (Fr. V / V Bis / XV) con umbral,
 * porcentaje alcanzado, semáforo y explicaciones de reinicio y avisos individuales.
 *
 * Consumo: GET /api/inmobiliaria/clientes/{id}/perfil-transaccional
 *
 * NOTA: exclusivo del giro inmobiliaria. Se renderiza SOLO en Cliente360 cuando
 * `user.giro==="inmobiliaria"` (el gate está en el consumidor, no aquí).
 * El motor de acumulación NO se toca — este componente sólo LEE lo que ya calcula.
 */
import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, ShieldAlert, AlertTriangle, RotateCcw, Info } from "lucide-react";
import { api } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// feb-2026 · iter 08-02 — Solo Fr. V se expone en UI (fase PLD PURA). El endpoint del
// perfil transaccional sigue devolviendo las 3 fracciones para no romper contratos; el
// widget filtra a solo aquellas que estén catalogadas aquí. Al reactivar V Bis o XV,
// basta con descomentar la línea correspondiente.
const FRACCION_LABEL = {
  V: "Fr. V · Compraventa e intermediación",
  // V_bis: "Fr. V Bis · Desarrollo (aportaciones)",  // pendiente
  // XV:    "Fr. XV · Arrendamiento",                 // pendiente
};

const SEM_STYLE = {
  verde: {
    Icon: ShieldCheck, label: "Lejos del umbral",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-300",
    bar: "bg-emerald-500",
    chip: "bg-emerald-50 text-emerald-700 border-emerald-300",
  },
  ambar: {
    Icon: AlertTriangle, label: "Cerca del umbral (≥80%)",
    pill: "bg-amber-50 text-amber-700 border-amber-300",
    bar: "bg-amber-500",
    chip: "bg-amber-50 text-amber-700 border-amber-300",
  },
  rojo: {
    Icon: ShieldAlert, label: "Umbral REBASADO",
    pill: "bg-rose-50 text-rose-700 border-rose-300",
    bar: "bg-rose-500",
    chip: "bg-rose-50 text-rose-700 border-rose-300",
  },
};

function Semaforo({ estado, size = "sm" }) {
  const cfg = SEM_STYLE[estado] || SEM_STYLE.verde;
  const { Icon, label, pill } = cfg;
  const isBig = size === "lg";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 ${isBig ? "text-sm" : "text-xs"} font-medium ${pill}`}
          data-testid={`ptx-semaforo-${estado}`}>
      <Icon className={isBig ? "h-4 w-4" : "h-3.5 w-3.5"} /> {label}
    </span>
  );
}

function BarraProgreso({ porcentaje, semaforo }) {
  const w = Math.min(porcentaje, 100);
  const cfg = SEM_STYLE[semaforo] || SEM_STYLE.verde;
  return (
    <div className="h-2 w-full rounded-full bg-slate-100" data-testid="ptx-barra">
      <div className={`h-2 rounded-full transition-all ${cfg.bar}`} style={{ width: `${w}%` }} />
    </div>
  );
}

function FraccionCard({ r }) {
  const { fraccion, acumulado_6m_mxn, umbral_aviso_mxn, falta_para_umbral_mxn,
          porcentaje_alcanzado, semaforo, num_operaciones_en_ventana,
          num_operaciones_historico_total, num_excluidas_por_aviso_individual,
          num_excluidas_por_reinicio_previo, fecha_ultima_operacion,
          reinicio_reciente } = r;
  const label = FRACCION_LABEL[fraccion] || fraccion;
  const sinOps = num_operaciones_historico_total === 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-3"
         data-testid={`ptx-fraccion-${fraccion}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
          <div className="mt-0.5 text-xl font-semibold text-slate-800" data-testid={`ptx-acum-${fraccion}`}>
            {mxn(acumulado_6m_mxn)}
          </div>
          <div className="text-xs text-slate-500">
            acumulado en la ventana móvil 6 meses
          </div>
        </div>
        <Semaforo estado={semaforo} />
      </div>

      <BarraProgreso porcentaje={porcentaje_alcanzado} semaforo={semaforo} />
      <div className="flex justify-between text-xs text-slate-600">
        <span>{porcentaje_alcanzado}% del umbral</span>
        <span>Umbral: {mxn(umbral_aviso_mxn)}</span>
      </div>

      {!sinOps && semaforo !== "rojo" && (
        <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700">
          Faltan <b>{mxn(falta_para_umbral_mxn)}</b> para el umbral de aviso.
        </div>
      )}
      {semaforo === "rojo" && (
        <div className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800">
          Este cliente <b>rebasó el umbral de aviso</b> en la fracción {fraccion}. Requiere aviso
          por acumulación en la próxima operación.
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
        <div>
          <div className="text-slate-400">Operaciones en ventana</div>
          <div className="font-medium text-slate-700">{num_operaciones_en_ventana}</div>
        </div>
        <div>
          <div className="text-slate-400">Histórico total</div>
          <div className="font-medium text-slate-700">{num_operaciones_historico_total}</div>
        </div>
        <div>
          <div className="text-slate-400">Última operación</div>
          <div className="font-medium text-slate-700">
            {fecha_ultima_operacion ? fdate(fecha_ultima_operacion) : "—"}
          </div>
        </div>
      </div>

      {reinicio_reciente && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
             data-testid={`ptx-reinicio-${fraccion}`}>
          <div className="mb-1 flex items-center gap-1.5 font-semibold">
            <RotateCcw className="h-3.5 w-3.5" /> El período se reinició el {fdate(reinicio_reciente.fecha)}
          </div>
          <div>
            La operación del {fdate(reinicio_reciente.fecha)} por {mxn(reinicio_reciente.monto_detonador_mxn)}
            {reinicio_reciente.acumulado_al_reiniciar_mxn != null && (
              <> alcanzó un acumulado de <b>{mxn(reinicio_reciente.acumulado_al_reiniciar_mxn)}</b>
                {" "}({reinicio_reciente.porcentaje_al_reiniciar}% del umbral)</>
            )}, lo que disparó el aviso por acumulación y <b>reinició el conteo</b>.
            Por eso el acumulado actual puede parecer bajo aunque haya operaciones previas.
          </div>
        </div>
      )}

      {num_excluidas_por_aviso_individual > 0 && (
        <div className="flex items-start gap-2 text-xs text-slate-500"
             data-testid={`ptx-excluidas-aviso-${fraccion}`}>
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <b>{num_excluidas_por_aviso_individual}</b> operación(es) generaron aviso individual
            (por sí solas rebasaron el umbral) y por ley no cuentan en la acumulación (evita
            doble conteo). Aparecen en el listado pero no suman aquí.
          </div>
        </div>
      )}
    </div>
  );
}

export function PerfilTransaccionalInmo({ clienteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: d } = await api.get(`/inmobiliaria/clientes/${clienteId}/perfil-transaccional`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "No se pudo cargar el perfil transaccional");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (clienteId) load();
    return () => { cancelled = true; };
  }, [clienteId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-slate-200 bg-white p-8"
           data-testid="ptx-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
           data-testid="ptx-error">{String(error)}</div>
    );
  }
  if (!data) return null;

  const cero = data.num_operaciones_totales_historico === 0;

  return (
    <Card data-testid="ptx-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base font-heading">
              Perfil transaccional · 6 meses
            </CardTitle>
            <div className="text-xs text-slate-500 mt-0.5">
              Ventana móvil 183 días · Umbral por fracción: {mxn(data.umbral_aviso_mxn_hoy)}
              {data.uma_diario_hoy_mxn && <> · UMA vigente: {mxn(data.uma_diario_hoy_mxn)}/día</>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Semaforo estado={data.peor_semaforo || "verde"} size="lg" />
            <div className="text-xs text-slate-500">
              Total operaciones históricas: <b data-testid="ptx-total-ops">{data.num_operaciones_totales_historico}</b>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {cero ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 text-center"
               data-testid="ptx-vacio">
            Este cliente aún no tiene operaciones registradas con esta entidad.
          </div>
        ) : (
          <>
            {/* feb-2026 · iter 08-02 — Sólo se muestran fracciones catalogadas en
                FRACCION_LABEL (fase PLD PURA = solo V). El endpoint sigue devolviendo
                V, V_bis y XV; el filtro aquí evita romper cuando se reactiven las otras. */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {data.por_fraccion
                .filter((r) => FRACCION_LABEL[r.fraccion])
                .map((r) => (
                  <FraccionCard key={r.fraccion} r={r} />
                ))}
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <div className="mb-1 flex items-center gap-1.5 font-medium">
                <Info className="h-3.5 w-3.5" /> Cómo leer este widget
              </div>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>El acumulado es <b>por fracción</b>. Cada una tiene su propio umbral. No se suman entre sí.</li>
                <li>Al rebasar el umbral, el motor <b>reinicia el período</b> automáticamente (Art. 17 penúltimo párrafo). El acumulado puede bajar de golpe: es correcto.</li>
                <li>Las operaciones que ya generaron <b>aviso individual</b> no cuentan en la acumulación (evita doble conteo).</li>
              </ul>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Chip pequeño usado en la lista de clientes para mostrar el peor semáforo.
export function SemaforoTx({ estado, porcentaje }) {
  if (!estado) return <span className="text-xs text-slate-300">—</span>;
  const cfg = SEM_STYLE[estado] || SEM_STYLE.verde;
  const { Icon, chip } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium ${chip}`}
          data-testid={`ptx-lista-sem-${estado}`}>
      <Icon className="h-3 w-3" /> {porcentaje != null ? `${porcentaje}%` : ""}
    </span>
  );
}
